import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  FacebookIntegration,
  GoHighLevelIntegration,
  GoogleDriveIntegration,
  GoogleIntegration,
  InstagramIntegration,
  LinkedInIntegration,
  MailchimpIntegration,
  MetaAdsIntegration,
  ShopifyIntegration,
  TikTokIntegration,
  WixIntegration,
  WordPressIntegration,
  XIntegration,
  YouTubeIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import PlaceholderAvatar from './PlaceholderAvatar.png'
import { absurd } from 'types'

const facebookIntegrationDetails = (integration: FacebookIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.picture?.data?.url || PlaceholderAvatar
  const username = name

  return { name, picture, username }
}

const instagramIntegrationDetails = (integration: InstagramIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.profile_picture_url || PlaceholderAvatar
  const username = integration.additional_data?.username

  return { name, picture, username }
}

const linkedinIntegrationDetails = (integration: LinkedInIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.picture || PlaceholderAvatar
  const username = integration.additional_data?.email || integration.name

  return { name, picture, username }
}

const xIntegrationDetails = (integration: XIntegration) => {
  const name = integration.name || integration.additional_data?.name
  const picture = integration.picture || integration.additional_data?.profile_image_url || PlaceholderAvatar
  const username = integration.additional_data?.username

  return { name, picture, username }
}

const wordpressIntegrationDetails = (integration: WordPressIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.url || ''

  return { name, picture, username }
}

const tiktokIntegrationDetails = (integration: TikTokIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.username || ''

  return { name, picture, username }
}

const youtubeIntegrationDetails = (integration: YouTubeIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.username || ''

  return { name, picture, username }
}

const googleIntegrationDetails = (integration: GoogleIntegration) => {
  const { default_uri } = integration.additional_data || {}
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = default_uri || ''

  return { name, picture, username }
}

const metaAdsIntegrationDetails = (integration: MetaAdsIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.name

  return { name, picture, username }
}

const goHighLevelIntegrationDetails = (integration: GoHighLevelIntegration) => {
  const name = integration.name
  const picture = integration.picture || PlaceholderAvatar
  const username = integration.additional_data?.user_name || ''

  return { name, picture, username }
}

interface MailchimpExtendedData {
  username?: string
  account_name?: string
  avatar_url?: string
}

const mailchimpIntegrationDetails = (integration: MailchimpIntegration) => {
  const extendedData = integration.additional_data as MailchimpExtendedData
  const username = extendedData?.username || integration.name
  return { name: username, picture: integration.picture || PlaceholderAvatar, username }
}

export const getIntegrationDetails = (integration?: ExternalIntegration, defaultName = 'Account Not Connected') => {
  if (!integration) {
    return { name: defaultName, picture: PlaceholderAvatar, username: 'youraccount' }
  }

  const { provider } = integration
  switch (provider) {
    case ExternalIntegrationEnum.WordPress:
      return wordpressIntegrationDetails(integration as WordPressIntegration)
    case ExternalIntegrationEnum.Facebook:
      return facebookIntegrationDetails(integration as FacebookIntegration)
    case ExternalIntegrationEnum.Instagram:
      return instagramIntegrationDetails(integration as InstagramIntegration)
    case ExternalIntegrationEnum.LinkedIn:
      return linkedinIntegrationDetails(integration as LinkedInIntegration)
    case ExternalIntegrationEnum.X:
      return xIntegrationDetails(integration as XIntegration)
    case ExternalIntegrationEnum.TikTok:
      return tiktokIntegrationDetails(integration as TikTokIntegration)
    case ExternalIntegrationEnum.YouTube:
      return youtubeIntegrationDetails(integration as YouTubeIntegration)
    case ExternalIntegrationEnum.Mailchimp:
      return mailchimpIntegrationDetails(integration as MailchimpIntegration)
    case ExternalIntegrationEnum.Google:
      return googleIntegrationDetails(integration as GoogleIntegration)
    case ExternalIntegrationEnum.GoogleDrive:
      return {
        name: (integration as GoogleDriveIntegration).name,
        picture: PlaceholderAvatar,
        username: (integration as GoogleDriveIntegration).name,
      }
    case ExternalIntegrationEnum.MetaAds:
      return metaAdsIntegrationDetails(integration as MetaAdsIntegration)
    case ExternalIntegrationEnum.GoHighLevel:
      return goHighLevelIntegrationDetails(integration as GoHighLevelIntegration)
    case ExternalIntegrationEnum.Wix:
      return {
        name: (integration as WixIntegration).name,
        picture: PlaceholderAvatar,
        username:
          (integration as WixIntegration).additional_data?.profile?.site_display_name ||
          (integration as WixIntegration).name,
      }
    case ExternalIntegrationEnum.Shopify:
      return {
        name: (integration as ShopifyIntegration).name,
        picture: PlaceholderAvatar,
        username: (integration as ShopifyIntegration).additional_data?.shop_domain || 'shopify',
      }
    default:
      absurd(provider)
      return { name: 'Account Not Connected', picture: PlaceholderAvatar, username: 'youraccount' }
  }
}
